import React, { useEffect, useRef, useState, useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Modal, Form, Input, Typography } from 'antd';
import {
  LockOutlined, SafetyCertificateOutlined, QuestionCircleOutlined, CloseOutlined,
  TeamOutlined, FileProtectOutlined, DollarOutlined, ExclamationCircleOutlined,
  BarChartOutlined, ShopOutlined, SafetyOutlined, DownloadOutlined, RocketOutlined,
} from '@ant-design/icons';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Footer from './partials/footer';
import Navbar from './partials/navbar';
import MobileNav from './partials/MobileNav';
import SubNav from './partials/SubNav';
// AppBreadcrumb supprimé du layout : chaque page affiche son propre breadcrumb
// pour éviter la redondance (1 seul breadcrumb visible par page).
import { LayoutProvider, useLayout } from './LayoutContext';
import './responsive-layout.css';
import './layout-theme.scss';
import { auditService, UserService } from '../../common/services';
import { AuthContext, LOCAL_STORAGE_KEYS } from '../../common/constants';
import { useIdleLogout, useKeyboardShortcuts, useVersionCheck } from '../../common/hooks';
import { useT } from '../../common/i18n';
import { getPageConfig } from '../../common/config/pageConfig';

const { Text } = Typography;

// Page tracker pour l'audit côté API
const PageTracker = () => {
  const location = useLocation();
  const lastPath = useRef('');

  useEffect(() => {
    if (location.pathname !== lastPath.current) {
      lastPath.current = location.pathname;
      const cfg = getPageConfig(location.pathname);
      auditService.trackPageVisit({
        page: location.pathname,
        title: cfg.title,
      }).catch(() => {});
    }
  }, [location.pathname]);

  return null;
};

// ─── Modale de changement de mot de passe obligatoire ─────────────────────
const ForceChangePasswordModal = () => {
  const t = useT();
  const { currentUser, setCurrentUser } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentUser?.mustChangePassword) setVisible(true);
  }, [currentUser]);

  const handleChangePassword = async (values) => {
    try {
      setLoading(true);
      await UserService.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      const updatedUser = { ...currentUser, mustChangePassword: false };
      setCurrentUser(updatedUser);
      localStorage.setItem(LOCAL_STORAGE_KEYS.CURRENT_USER, JSON.stringify(updatedUser));
      toast.success(t('force_password.success'));
      setVisible(false);
      form.resetFields();
    } catch (error) {
      toast.error(error?.message || t('force_password.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SafetyCertificateOutlined style={{ color: '#C8102E', fontSize: 22 }} />
          <span>{t('force_password.title')}</span>
        </div>
      }
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={450}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">{t('force_password.intro')}</Text>
      </div>

      <Form form={form} layout="vertical" onFinish={handleChangePassword}>
        <Form.Item
          name="currentPassword"
          label={t('force_password.current')}
          rules={[{ required: true, message: t('common.required') }]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: 'var(--cap-text-muted)' }} />}
            placeholder={t('force_password.current')}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label={t('force_password.new')}
          rules={[
            { required: true, message: t('common.required') },
            { min: 6, message: t('force_password.min') },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: 'var(--cap-text-muted)' }} />}
            placeholder={t('force_password.new')}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={t('force_password.confirm')}
          dependencies={['newPassword']}
          rules={[
            { required: true, message: t('common.required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error(t('force_password.mismatch')));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: 'var(--cap-text-muted)' }} />}
            placeholder={t('force_password.confirm')}
            style={{ borderRadius: 8 }}
          />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              height: 44,
              backgroundColor: '#C8102E',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t('force_password.submitting') : t('force_password.submit')}
          </button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Idle logout warning ─────────────────────────────────────────────────
const IdleLogoutGuard = () => {
  const t = useT();
  const { onLogout } = useContext(AuthContext);
  const [warningOpen, setWarningOpen] = useState(false);
  const { reset } = useIdleLogout({
    timeoutMs: 30 * 60 * 1000,
    warningMs: 60 * 1000,
    onWarning: () => setWarningOpen(true),
    onLogout: () => {
      setWarningOpen(false);
      onLogout?.();
    },
  });
  return (
    <Modal
      open={warningOpen}
      title={t('a11y.session_warning_title')}
      okText={t('auth.session.stay_connected')}
      cancelText={t('nav.logout')}
      onOk={() => { setWarningOpen(false); reset(); }}
      onCancel={() => { setWarningOpen(false); onLogout?.(); }}
      maskClosable={false}
      closable={false}
    >
      <p>{t('auth.session.idle_warning', { seconds: 60 })}</p>
    </Modal>
  );
};

// ─── Modale raccourcis clavier (déclenchable via cap:open-help) ──────────
const ShortcutsHelp = ({ open, onClose }) => {
  const t = useT();
  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--cap-divider)' };
  const kbdStyle = { background: 'var(--cap-surface-2)', padding: '2px 8px', borderRadius: 4, border: '1px solid var(--cap-border)', fontFamily: 'monospace' };
  return (
    <Modal open={open} onCancel={onClose} title={t('nav.help')} footer={null} width={420}>
      <div style={{ fontSize: 13 }}>
        <div style={rowStyle}>
          <span>Focus la recherche</span>
          <kbd style={kbdStyle}>/</kbd>
        </div>
        <div style={rowStyle}>
          <span>Fermer modal / dropdown</span>
          <kbd style={kbdStyle}>Esc</kbd>
        </div>
        <div style={rowStyle}>
          <span>Afficher cette aide</span>
          <kbd style={kbdStyle}>?</kbd>
        </div>
        <div style={{ ...rowStyle, borderBottom: 'none' }}>
          <span>Tab / Maj+Tab</span>
          <span style={{ color: 'var(--cap-text-muted)' }}>Naviguer entre les éléments</span>
        </div>
      </div>
    </Modal>
  );
};

// ─── Centre d'aide global (déclenchable via cap:open-helpcenter) ────────
const HelpCenterModal = ({ open, onClose }) => {
  const secStyle = {
    display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
    background: 'var(--cap-surface-2, #f6f8fb)', border: '1px solid var(--cap-border, #E8ECF1)',
    borderRadius: 12, lineHeight: 1.55,
  };
  const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
  const noteStyle = {
    marginTop: 8, padding: '10px 12px', background: 'rgba(200,16,46,0.07)',
    border: '1px solid rgba(200,16,46,0.25)', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.45,
  };
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Centre d'aide — Comment utiliser l'application</span>}
      closeIcon={(
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
          background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
          fontSize: '0.9rem', fontWeight: 600, color: '#fff',
        }}>
          <CloseOutlined /> Fermer
        </span>
      )}
      footer={<button onClick={onClose} className="user-dropdown-item" style={{ background: '#1A2B5F', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}>J'ai compris</button>}
      width={720}
      style={{ top: 40 }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 }}>
        <span style={{ color: 'var(--cap-text-muted, #8c8c8c)' }}>
          CAP Assurances est l'outil de gestion du cabinet de courtage. Voici le parcours
          complet : <strong>Client → Contrat → Règlement → Suivi (Impayés / Sinistres) →
          Rapports & Bordereaux</strong>. Chaque page possède aussi son propre bouton
          « Guide » pour une aide contextuelle.
        </span>
        <div style={{ marginTop: 16 }}>
          <div style={secStyle}>
            <RocketOutlined style={icStyle} />
            <div>
              <strong>Le parcours en bref</strong> — On crée d'abord un <strong>client</strong>,
              puis on lui souscrit un <strong>contrat</strong>. Les <strong>règlements</strong>
              encaissent les paiements ; ce qui reste dû apparaît en <strong>impayés</strong>.
              Les <strong>sinistres</strong> suivent les déclarations. Les <strong>rapports</strong>
              et <strong>bordereaux</strong> consolident le tout pour la comptabilité.
            </div>
          </div>
          <div style={secStyle}>
            <BarChartOutlined style={icStyle} />
            <div>
              <strong>Tableau de bord</strong> — Vue synthétique : chiffre d'affaires,
              production, encaissé, à recouvrer, sinistres, échéances. Les « Actions urgentes »
              signalent ce qui demande une intervention. Le sélecteur de période
              (30 j / 3 / 6 / 12 mois) ajuste tous les indicateurs.
            </div>
          </div>
          <div style={secStyle}>
            <TeamOutlined style={icStyle} />
            <div>
              <strong>Gestion › Clients</strong> — Le répertoire des particuliers et
              entreprises. Un client doit exister <em>avant</em> de pouvoir lui créer un
              contrat (bouton « Nouveau client »).
            </div>
          </div>
          <div style={secStyle}>
            <FileProtectOutlined style={icStyle} />
            <div>
              <strong>Gestion › Contrats</strong> — Les polices souscrites et leur statut.
              « Nouveau contrat » lance un assistant en 4 étapes (Souscripteur → Contrat →
              Prime → Confirmation).
            </div>
          </div>
          <div style={secStyle}>
            <DollarOutlined style={icStyle} />
            <div>
              <strong>Gestion › Règlements</strong> — Enregistre les paiements reçus
              (total ou partiel) sur un contrat. C'est ici que se saisit l'encaissement.
            </div>
          </div>
          <div style={secStyle}>
            <ExclamationCircleOutlined style={icStyle} />
            <div>
              <strong>Gestion › Impayés &amp; Sinistres</strong> — Les contrats avec un solde
              dû (relance SMS possible) et le suivi des déclarations de sinistres jusqu'à
              clôture.
            </div>
          </div>
          <div style={secStyle}>
            <BarChartOutlined style={icStyle} />
            <div>
              <strong>Rapports</strong> — État des règlements, Bilans &amp; États, Bordereau
              de production et État des comptes clients : documents comptables exportables en
              PDF / Excel selon les filtres.
            </div>
          </div>
          <div style={secStyle}>
            <ShopOutlined style={icStyle} />
            <div>
              <strong>Sélecteur d'agence (en haut)</strong> — Filtre toute l'application sur
              une agence.
              <div style={noteStyle}>
                <strong style={{ color: '#C8102E' }}>À savoir :</strong> si votre rôle n'est
                pas multi-agences, l'application est verrouillée sur votre agence (vous ne
                voyez que ses données) — c'est normal et garanti côté serveur.
              </div>
            </div>
          </div>
          <div style={secStyle}>
            <SafetyOutlined style={icStyle} />
            <div>
              <strong>Administration &amp; rôles</strong> — Gestion des administrateurs, des
              rôles et de leurs permissions (réservé aux profils habilités). À la sélection
              d'un rôle, ses permissions sont pré-cochées et restent ajustables.
            </div>
          </div>
          <div style={{ ...secStyle, marginBottom: 0 }}>
            <DownloadOutlined style={icStyle} />
            <div>
              <strong>Exports &amp; astuces</strong> — Le bouton « Exporter » génère PDF/Excel
              en tenant compte des filtres (période, agence, compagnie). Astuce : appuyez sur
              <kbd style={{ background: 'var(--cap-surface-2)', padding: '1px 6px', borderRadius: 4, border: '1px solid var(--cap-border)', fontFamily: 'monospace', margin: '0 4px' }}>?</kbd>
              pour les raccourcis clavier, et utilisez le bouton « Guide » de chaque page pour
              l'aide détaillée de cette page.
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const LayoutShell = () => {
  const t = useT();
  const { sidebarOpen, isXLUp, closeSidebar } = useLayout();
  const { helpOpen, setHelpOpen } = useKeyboardShortcuts();
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);

  // Permet à la navbar (et n'importe quel composant) d'ouvrir le modal d'aide
  useEffect(() => {
    const handler = () => setHelpOpen(true);
    window.addEventListener('cap:open-help', handler);
    return () => window.removeEventListener('cap:open-help', handler);
  }, [setHelpOpen]);

  // Propose de recharger quand une nouvelle version du backoffice est déployée
  useVersionCheck();

  // Centre d'aide global (icône ? de la barre du haut)
  useEffect(() => {
    const handler = () => setHelpCenterOpen(true);
    window.addEventListener('cap:open-helpcenter', handler);
    return () => window.removeEventListener('cap:open-helpcenter', handler);
  }, []);

  return (
    <>
      <a href="#main-content" className="skip-link">{t('a11y.skip_to_content')}</a>
      <PageTracker />
      <ToastContainer />
      <ForceChangePasswordModal />
      <IdleLogoutGuard />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <HelpCenterModal open={helpCenterOpen} onClose={() => setHelpCenterOpen(false)} />
      <div className={`layout-wrapper layout-topnav ${sidebarOpen ? 'has-sidebar-open' : ''}`}>
        {/* Topbar sticky niveau 1 : modules (Dashboard, Gestion, Rapports...) */}
        <header className="layout-header"><Navbar /></header>

        {/* Sub-nav sticky niveau 2 : sous-pages du module actif (two-tier nav) */}
        {isXLUp && <SubNav />}

        {/* Drawer mobile (design natif aligné sur la topnav + accordéons) */}
        {!isXLUp && sidebarOpen && (
          <>
            <MobileNav onClose={closeSidebar} />
            <div className="layout-overlay layout-menu-toggle show" onClick={closeSidebar} />
          </>
        )}

        {/* Content pleine largeur — chaque page gère son propre breadcrumb */}
        <main id="main-content" className="content-wrapper" role="main" tabIndex={-1}>
          <div className="page-content">
            <Outlet />
          </div>
          <Footer />
        </main>
      </div>
    </>
  );
};

const MainLayout = () => (
  <LayoutProvider>
    <LayoutShell />
  </LayoutProvider>
);

export default MainLayout;
